/**
 * @file Stores and restores intended post-auth destinations.
 */

import {
    isDetailLikePath,
    isDetailPath,
    parseDetailPath,
} from "../routing/detailRoutes.js";
import {
    isDrugLikePath,
    parseDrugPath,
} from "../routing/drugRoutes.js";
import {
    isCalendarLikePath,
    parseCalendarPath,
} from "../routing/calendarRoutes.js";
import {
    isRegisteredBoardViewKey,
} from "../../shared/board/boardRegistry.js";

const POST_AUTH_DESTINATION_KEY = "postAuthDestination";
const KNOWN_VIEWS = new Set([
    "tasks",
    "patients",
    "projects",
    "assets",
    "calendar",
    "drugs",
    "recalls",
    "ordering",
    "compliance",
    "settings",
    "sandbox",
]);

function isKnownViewPath(pathname) {
    if (!pathname || pathname === "/") {return false;}
    if (parseDrugPath(pathname)) {return true;}
    if (parseCalendarPath(pathname)) {return true;}
    const view = pathname.replace(/^\//, "");
    return KNOWN_VIEWS.has(view) || isRegisteredBoardViewKey(view);
}

function getKnownViewOrDefault(view) {
    return KNOWN_VIEWS.has(view) || isRegisteredBoardViewKey(view) ?
        view :
        "tasks";
}

function buildDestination({
    pathname = window.location.pathname,
    search = window.location.search,
    hash = window.location.hash,
    subView = null,
    itemType = null,
} = {}) {
    const normalizedPath = pathname || "/";
    const normalizedSearch = search || "";
    const normalizedHash = hash || "";
    const fullPath = `${normalizedPath}${normalizedSearch}${normalizedHash}`;

    if (normalizedPath === "/") {
        return {
            version: 1,
            path: "/",
            pathname: "/",
            search: normalizedSearch,
            hash: normalizedHash,
            view: "tasks",
            itemId: null,
            subView,
            savedAt: Date.now(),
        };
    }

    if (isDetailPath(normalizedPath)) {
        const detailRoute = parseDetailPath(normalizedPath);
        if (!detailRoute) {return null;}
        return {
            version: 1,
            path: fullPath,
            pathname: normalizedPath,
            search: normalizedSearch,
            hash: normalizedHash,
            view: "details",
            itemId: detailRoute.itemId,
            itemType: itemType || detailRoute.itemType,
            routeStatus: "valid",
            subView,
            savedAt: Date.now(),
        };
    }

    if (isDetailLikePath(normalizedPath)) {
        return {
            version: 1,
            path: fullPath,
            pathname: normalizedPath,
            search: normalizedSearch,
            hash: normalizedHash,
            view: "details",
            itemId: null,
            itemType: null,
            routeStatus: "invalid",
            subView,
            savedAt: Date.now(),
        };
    }

    const drugRoute = parseDrugPath(normalizedPath);
    if (drugRoute) {
        return {
            version: 1,
            path: fullPath,
            pathname: normalizedPath,
            search: normalizedSearch,
            hash: normalizedHash,
            view: "drugs",
            itemId: null,
            itemType: null,
            routeStatus: "valid",
            subView: drugRoute.subView || subView,
            savedAt: Date.now(),
        };
    }

    if (isDrugLikePath(normalizedPath)) {
        return {
            version: 1,
            path: fullPath,
            pathname: normalizedPath,
            search: normalizedSearch,
            hash: normalizedHash,
            view: "drugs",
            itemId: null,
            itemType: null,
            routeStatus: "invalid",
            subView,
            savedAt: Date.now(),
        };
    }

    const calendarRoute = parseCalendarPath(normalizedPath);
    if (calendarRoute) {
        return {
            version: 1,
            path: fullPath,
            pathname: normalizedPath,
            search: normalizedSearch,
            hash: normalizedHash,
            view: "calendar",
            itemId: null,
            itemType: null,
            routeStatus: "valid",
            subView: calendarRoute.subView || subView,
            savedAt: Date.now(),
        };
    }

    if (isCalendarLikePath(normalizedPath)) {
        return {
            version: 1,
            path: fullPath,
            pathname: normalizedPath,
            search: normalizedSearch,
            hash: normalizedHash,
            view: "calendar",
            itemId: null,
            itemType: null,
            routeStatus: "invalid",
            subView,
            savedAt: Date.now(),
        };
    }

    const view = normalizedPath.replace(/^\//, "");
    return {
        version: 1,
        path: fullPath,
        pathname: normalizedPath,
        search: normalizedSearch,
        hash: normalizedHash,
        view: getKnownViewOrDefault(view),
        itemId: null,
        itemType: null,
        routeStatus: null,
        subView,
        savedAt: Date.now(),
    };
}

/**
 * Returns whether the path should be preserved across auth.
 * @param {string} [pathname]
 * @returns {boolean}
 */
export function isProtectedDestinationPath(pathname = window.location.pathname) {
    return isDetailLikePath(pathname) ||
        isKnownViewPath(pathname) ||
        isDrugLikePath(pathname) ||
        isCalendarLikePath(pathname);
}

/**
 * Saves the current path as the intended post-auth destination.
 * @param {object} [options] Optional destination override.
 */
export function capturePostAuthDestination(options = {}) {
    const destination = buildDestination(options);
    if (!destination) {return;}
    sessionStorage.setItem(POST_AUTH_DESTINATION_KEY, JSON.stringify(destination));
}

/**
 * Reads the stored post-auth destination.
 * @returns {object|null} The stored destination.
 */
export function readPostAuthDestination() {
    const raw = sessionStorage.getItem(POST_AUTH_DESTINATION_KEY);
    if (!raw) {return null;}

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {return null;}
        return parsed;
    } catch (error) {
        console.warn("Invalid post-auth destination payload:", error);
        return null;
    }
}

/**
 * Clears the stored post-auth destination.
 */
export function clearPostAuthDestination() {
    sessionStorage.removeItem(POST_AUTH_DESTINATION_KEY);
}

/**
 * Captures the current path as the intended post-auth destination when relevant.
 * @param {object} [options] Optional destination override.
 * @returns {object|null} The captured or existing destination.
 */
export function ensurePostAuthDestination(options = {}) {
    const existingDestination = readPostAuthDestination();
    if (existingDestination) {
        return existingDestination;
    }

    const pathname = options.pathname || window.location.pathname;
    if (!isProtectedDestinationPath(pathname)) {
        return null;
    }

    capturePostAuthDestination(options);
    return readPostAuthDestination();
}
